import { resolveValidStoreId } from "@medicorepay/shared";
import {
  ArrowLeft,
  Building2,
  FileBadge2,
  Info,
  Loader2,
  Save,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { showToast } from "../../lib/toast";
import {
  type PharmacyInformation,
  storeService,
  type UpdatePharmacyInformationData,
} from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";

type RegistrationType = "BASIC" | "STATE" | "CENTRAL";

interface FormState {
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  gstNumber: string;
  licenseNumber: string;
  licenseExpiry: string;
  fssaiNumber: string;
  fssaiRegistrationType: RegistrationType;
  fssaiIssueDate: string;
  fssaiExpiryDate: string;
  fssaiProductsAllowed: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  phone: "",
  gstNumber: "",
  licenseNumber: "",
  licenseExpiry: "",
  fssaiNumber: "",
  fssaiRegistrationType: "STATE",
  fssaiIssueDate: "",
  fssaiExpiryDate: "",
  fssaiProductsAllowed: "",
};

const toDateInput = (value: string | null | undefined) =>
  value ? value.slice(0, 10) : "";

const inputClass =
  "mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";

function formFromInformation(data: PharmacyInformation): FormState {
  return {
    name: data.store.name,
    address: data.store.address,
    city: data.store.city,
    state: data.store.state,
    pincode: data.store.pincode,
    phone: data.store.phone ?? "",
    gstNumber: data.store.gstNumber ?? "",
    licenseNumber: data.store.licenseNumber ?? "",
    licenseExpiry: toDateInput(data.store.licenseExpiry),
    fssaiNumber: data.fssaiRegistration?.fssaiNumber ?? "",
    fssaiRegistrationType:
      (data.fssaiRegistration?.registrationType as RegistrationType) ?? "STATE",
    fssaiIssueDate: toDateInput(data.fssaiRegistration?.issueDate),
    fssaiExpiryDate: toDateInput(data.fssaiRegistration?.expiryDate),
    fssaiProductsAllowed: data.fssaiRegistration?.productsAllowed ?? "",
  };
}

function errorMessage(error: unknown, fallback: string) {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "data" in error.response
  ) {
    const data = error.response.data as { message?: string | string[] };
    if (Array.isArray(data.message)) return data.message.join(". ");
    if (data.message) return data.message;
  }
  return fallback;
}

export function PharmacyInformationPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("settings:update");
  const selectedStoreId = useAuthStore((state) =>
    resolveValidStoreId(
      state.selectedStoreId,
      state.user?.storeId,
      state.user?.stores,
    ),
  );
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [information, setInformation] = useState<PharmacyInformation | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadInformation = useCallback(async () => {
    if (!selectedStoreId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await storeService.getPharmacyInformation(selectedStoreId);
      setInformation(data);
      setForm(formFromInformation(data));
    } catch (error) {
      showToast.error(
        errorMessage(error, "Unable to load pharmacy information"),
      );
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    void loadInformation();
  }, [loadInformation]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const validate = () => {
    if (
      !form.name.trim() ||
      !form.address.trim() ||
      !form.city.trim() ||
      !form.state.trim()
    ) {
      return "Store name and complete address are required";
    }
    if (!/^[1-9][0-9]{5}$/.test(form.pincode.trim())) {
      return "Enter a valid 6-digit pincode";
    }
    if (!/^[6-9][0-9]{9}$/.test(form.phone.trim())) {
      return "Enter a valid 10-digit Indian phone number";
    }
    if (
      form.gstNumber.trim() &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
        form.gstNumber.trim().toUpperCase(),
      )
    ) {
      return "Enter a valid 15-character GSTIN";
    }
    if (form.fssaiNumber.trim()) {
      if (!/^\d{14}$/.test(form.fssaiNumber.trim())) {
        return "FSSAI number must contain 14 digits";
      }
      if (!form.fssaiIssueDate || !form.fssaiExpiryDate) {
        return "FSSAI issue and expiry dates are required";
      }
      if (form.fssaiExpiryDate <= form.fssaiIssueDate) {
        return "FSSAI expiry date must be after the issue date";
      }
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedStoreId || !canUpdate) return;

    const validationError = validate();
    if (validationError) {
      showToast.error(validationError);
      return;
    }

    const payload: UpdatePharmacyInformationData = {
      name: form.name.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      phone: form.phone.trim(),
      gstNumber: form.gstNumber.trim().toUpperCase() || null,
      licenseNumber: form.licenseNumber.trim().toUpperCase() || null,
      licenseExpiry: form.licenseExpiry || null,
      fssai: form.fssaiNumber.trim()
        ? {
            fssaiNumber: form.fssaiNumber.trim(),
            registrationType: form.fssaiRegistrationType,
            issueDate: form.fssaiIssueDate,
            expiryDate: form.fssaiExpiryDate,
            productsAllowed: form.fssaiProductsAllowed.trim() || null,
          }
        : null,
    };

    try {
      setSaving(true);
      const data = await storeService.updatePharmacyInformation(
        selectedStoreId,
        payload,
      );
      setInformation(data);
      setForm(formFromInformation(data));
      showToast.success("Pharmacy information saved");
    } catch (error) {
      showToast.error(
        errorMessage(error, "Unable to save pharmacy information"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-80 items-center justify-center text-sm text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading pharmacy information...
      </div>
    );
  }

  if (!selectedStoreId) {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Select a store before editing pharmacy information.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6 pb-10">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/settings")}
            className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-teal-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to settings
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Pharmacy Information
          </h1>
          <p className="mt-1 text-sm text-slate-500 sm:text-base">
            Maintain the legal and store details printed on invoices.
          </p>
        </div>
        {canUpdate && (
          <button
            type="submit"
            form="pharmacy-information-form"
            disabled={saving}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </button>
        )}
      </header>

      {!canUpdate && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          You can view these details, but your role does not allow settings
          updates.
        </div>
      )}

      <form
        id="pharmacy-information-form"
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            icon={Building2}
            title="Store identity and contact"
            description="The public identity and address used in the invoice header."
            iconClass="bg-sky-50 text-sky-600"
          />
          <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
            <Field label="Store name" required className="sm:col-span-2">
              <input
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
                disabled={!canUpdate}
                maxLength={100}
                className={inputClass}
              />
            </Field>
            <Field label="Street address" required className="sm:col-span-2">
              <input
                value={form.address}
                onChange={(event) => setField("address", event.target.value)}
                disabled={!canUpdate}
                maxLength={500}
                className={inputClass}
              />
            </Field>
            <Field label="City" required>
              <input
                value={form.city}
                onChange={(event) => setField("city", event.target.value)}
                disabled={!canUpdate}
                maxLength={100}
                className={inputClass}
              />
            </Field>
            <Field label="State" required>
              <input
                value={form.state}
                onChange={(event) => setField("state", event.target.value)}
                disabled={!canUpdate}
                maxLength={100}
                className={inputClass}
              />
            </Field>
            <Field label="Pincode" required>
              <input
                inputMode="numeric"
                value={form.pincode}
                onChange={(event) => setField("pincode", event.target.value)}
                disabled={!canUpdate}
                maxLength={6}
                className={inputClass}
              />
            </Field>
            <Field label="Store phone" required>
              <input
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={(event) => setField("phone", event.target.value)}
                disabled={!canUpdate}
                maxLength={10}
                className={inputClass}
              />
            </Field>
          </div>
          {information && (
            <div className="border-t border-slate-100 bg-slate-50 px-5 py-3 text-xs text-slate-500 sm:px-6">
              Account contact: {information.accountContact.email}
              {information.accountContact.phone
                ? ` · ${information.accountContact.phone}`
                : ""}
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            icon={FileBadge2}
            title="Tax and Drug Licence"
            description="GSTIN and the primary licence reference shown on invoices."
            iconClass="bg-indigo-50 text-indigo-600"
          />
          <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
            <Field label="GSTIN" hint="15 characters; optional">
              <input
                value={form.gstNumber}
                onChange={(event) =>
                  setField("gstNumber", event.target.value.toUpperCase())
                }
                disabled={!canUpdate}
                maxLength={15}
                placeholder="27ABCDE1234F1Z5"
                className={inputClass}
              />
            </Field>
            <div className="hidden sm:block" />
            <Field label="Primary DL number" hint="Invoice-facing reference">
              <input
                value={form.licenseNumber}
                onChange={(event) =>
                  setField("licenseNumber", event.target.value.toUpperCase())
                }
                disabled={!canUpdate}
                maxLength={50}
                placeholder="DL-20B-123456"
                className={inputClass}
              />
            </Field>
            <Field label="DL expiry date">
              <input
                type="date"
                value={form.licenseExpiry}
                onChange={(event) =>
                  setField("licenseExpiry", event.target.value)
                }
                disabled={!canUpdate}
                className={inputClass}
              />
            </Field>
          </div>
          <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <span>
              Use the Drug Licences register for issuing authority, pharmacist,
              schedules and documents.
            </span>
            <button
              type="button"
              onClick={() => navigate("/drug-licenses")}
              className="w-fit font-semibold text-teal-700 hover:text-teal-800"
            >
              Open Drug Licences
            </button>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            icon={ShieldCheck}
            title="FSSAI registration"
            description="Optional registration details for stores selling regulated food products."
            iconClass="bg-emerald-50 text-emerald-600"
          />
          <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
            <Field label="FSSAI number" hint="14 digits; optional">
              <input
                inputMode="numeric"
                value={form.fssaiNumber}
                onChange={(event) =>
                  setField("fssaiNumber", event.target.value.replace(/\D/g, ""))
                }
                disabled={!canUpdate}
                maxLength={14}
                placeholder="12345678901234"
                className={inputClass}
              />
            </Field>
            <Field label="Registration type">
              <select
                value={form.fssaiRegistrationType}
                onChange={(event) =>
                  setField(
                    "fssaiRegistrationType",
                    event.target.value as RegistrationType,
                  )
                }
                disabled={!canUpdate || !form.fssaiNumber}
                className={inputClass}
              >
                <option value="BASIC">Basic Registration</option>
                <option value="STATE">State Licence</option>
                <option value="CENTRAL">Central Licence</option>
              </select>
            </Field>
            <Field label="Issue date">
              <input
                type="date"
                value={form.fssaiIssueDate}
                onChange={(event) =>
                  setField("fssaiIssueDate", event.target.value)
                }
                disabled={!canUpdate || !form.fssaiNumber}
                className={inputClass}
              />
            </Field>
            <Field label="Expiry date">
              <input
                type="date"
                value={form.fssaiExpiryDate}
                onChange={(event) =>
                  setField("fssaiExpiryDate", event.target.value)
                }
                disabled={!canUpdate || !form.fssaiNumber}
                className={inputClass}
              />
            </Field>
            <Field
              label="Products allowed"
              hint="Optional summary from the registration"
              className="sm:col-span-2"
            >
              <input
                value={form.fssaiProductsAllowed}
                onChange={(event) =>
                  setField("fssaiProductsAllowed", event.target.value)
                }
                disabled={!canUpdate || !form.fssaiNumber}
                maxLength={500}
                placeholder="Nutraceuticals, packaged foods"
                className={inputClass}
              />
            </Field>
          </div>
        </section>
      </form>
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  description,
  iconClass,
}: {
  icon: typeof Building2;
  title: string;
  description: string;
  iconClass: string;
}) {
  return (
    <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClass}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h2 className="font-semibold text-slate-900">{title}</h2>
        <p className="mt-0.5 text-sm text-slate-500">{description}</p>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  className = "",
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="text-sm font-semibold text-slate-700">
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </span>
      {hint && <span className="ml-2 text-xs text-slate-400">{hint}</span>}
      {children}
    </label>
  );
}

export default PharmacyInformationPage;
